import { Data, Modal, UI, Utils } from 'common';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getUserType } from '../../utils/userType';
import MobileAppDownloadModal from '../mobileAppDownload/MobileAppDownloadModal.component';

const { TodoTaskItem, Typography, Spinner } = UI;
const { WrappedButton, WrappedButtonType } = UI;
const { Analytics, BillingPlans, Navigation, Session, Communication, Util } =
	Utils;
const { SegmentEventTypes, PlanIds, Capability } = Data;
const { ModalContext } = Modal;

const TASK_IDS = {
	STOCK_UP_ESSENTIALS: 'stockUpEssentials',
	BUILD_ADDRESS_BOOK: 'buildAddressBook',
	MOBILE_APP_DOWNLOAD: 'mobileAppDownload',
	ADD_LOGO: 'addLogo',
	ADD_COST_CODES: 'addCostCodes',
	ADD_USERS: 'addUsers',
	REFUND_ASSIST: 'refundAssist',
	REVIEW_SUBSCRIPTION_PLAN: 'reviewSubscriptionPlan',
} as const;

type TaskId = (typeof TASK_IDS)[keyof typeof TASK_IDS];

type Task = {
	id: TaskId;
	header: string;
	text: string;
	buttonText: string;
	isCompleted: boolean;
};

type DismissedTasksState = {
	[taskId: string]: 0 | 1;
};

const getTasks = (consumer: boolean): Task[] => {
	const tasks: Task[] = [
		{
			id: TASK_IDS.STOCK_UP_ESSENTIALS,
			header: consumer
				? 'Stock up on supplies.'
				: 'Stock up on shipping and mailing essentials.',
			text: 'Get label sheets, envelopes, boxes, and free USPS® supplies from our online store.',
			buttonText: 'Shop Now',
			isCompleted: false,
		},
		{
			id: TASK_IDS.BUILD_ADDRESS_BOOK,
			header: 'Build your address book.',
			text: 'Add at least 10 frequent contacts to save time each time you mail or ship.',
			buttonText: 'Add Contacts',
			isCompleted: false,
		},
	];

	if (consumer) {
		tasks.push({
			id: TASK_IDS.MOBILE_APP_DOWNLOAD,
			header: 'Download the Stamps.com mobile app.',
			text: 'Print over Wi-Fi or create QR code labels to be scanned by the carrier.',
			buttonText: 'Download',
			isCompleted: false,
		});
	} else {
		tasks.push(
			{
				id: TASK_IDS.ADD_LOGO,
				header: 'Turn sent items into a marketing opportunity.',
				text: 'Add your logo or graphics to customize your shipping labels and envelopes.',
				buttonText: 'Add Logo',
				isCompleted: false,
			},
			{
				id: TASK_IDS.ADD_COST_CODES,
				header: 'Simplify expense reporting.',
				text: 'Set up cost codes to associate mail and shipments with specific clients or projects.',
				buttonText: 'Add Cost Codes',
				isCompleted: false,
			},
			{
				id: TASK_IDS.ADD_USERS,
				header: 'Delegate mailing and shipping tasks.',
				text: 'Add users to your account to streamline your shipping operations.',
				buttonText: 'Add Users',
				isCompleted: false,
			}
		);
	}

	tasks.push({
		id: TASK_IDS.REVIEW_SUBSCRIPTION_PLAN,
		header: 'Review subscription plan options.',
		text: 'Make sure you are getting all of the benefits of Stamps.com!',
		buttonText: 'Review Subscription Plans',
		isCompleted: false,
	});

	tasks.push({
		id: TASK_IDS.REFUND_ASSIST,
		header: 'Get money back on unused labels.',
		text: 'Refund Assist identifies your unused, eligible USPS® labels and handles the entire refund process for you.',
		buttonText: 'Learn More',
		isCompleted: false,
	});

	return tasks;
};

/**
 * Get dismissed tasks state from user preferences
 * Returns object with task IDs as keys and 0/1 as values
 * Example: { "stockUpEssentials": 0, "buildAddressBook": 1 }
 * 0 = not dismissed (visible), 1 = dismissed (hidden)
 */
const getDismissedTasksState = (): DismissedTasksState => {
	const preferences = Session.getPreferences();
	const dismissedTasksString = preferences['DismissedWelcomeTodoTasks'];

	if (dismissedTasksString) {
		return JSON.parse(dismissedTasksString) as DismissedTasksState;
	}

	return {};
};

/**
 * Save dismissed tasks state to user preferences
 */
const saveDismissedTasksState = (dismissedState: DismissedTasksState): void => {
	try {
		Session.savePreferences([
			{
				Name: 'DismissedWelcomeTodoTasks',
				Value: JSON.stringify(dismissedState),
			},
		]);
	} catch (error) {
		console.error('Failed to save dismissed tasks state:', error);
	}
};

/**
 * TodoTasks Component
 * Main container for the to-do tasks list with expandable/collapsible functionality
 */
const TodoTasks: React.FC = () => {
	const consumer = getUserType() === 'consumer';
	const modalContext = useContext(ModalContext);
	const { showModal } = modalContext || {};
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [dismissedState, setDismissedState] = useState<DismissedTasksState>({});
	const [allTasks] = useState<Task[]>(getTasks(consumer));
	const timeoutRefs = useRef<{
		[taskId: string]: ReturnType<typeof setTimeout>;
	}>({});
	const [hasContacts, setHasContacts] = useState<boolean>(false);
	const [hasCostCodes, setHasCostCodes] = useState<boolean>(false);
	const [hasLogo, setHasLogo] = useState<boolean>(false);
	const [hasUsers, setHasUsers] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { customerId } = Session.getAccountInfo();

	// Plan IDs that should hide 'Add Logo' and 'Add Users' tasks for business users
	const planId = Session.getAccountInfo()?.planDetails?.planId;
	const isBusiness = getUserType() === 'business';
	const officePlanIds = [PlanIds.Office, PlanIds.OfficeV381];
	const ecommercePlanIds = [PlanIds.Ecommerce, PlanIds.EcommerceV383];
	const shouldShowLogoAndUsersTasks =
		isBusiness &&
		planId &&
		(officePlanIds.includes(planId) || ecommercePlanIds.includes(planId));

	// Load dismissed tasks state from user preferences on mount
	useEffect(() => {
		const dismissed = getDismissedTasksState();
		setDismissedState(dismissed);
	}, []);

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			Object.values(timeoutRefs.current).forEach(timeout => {
				clearTimeout(timeout);
			});
		};
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const fetchContacts = async () => {
					const response = await Communication.getContacts();
					if (Communication.isSuccessful(response)) {
						if (response?.data?.TotalCount > 0) {
							setHasContacts(true);
						}
					}
				};

				const fetchCostCodes = async () => {
					const costCodes = await Communication.getCostCodes(customerId);
					if (costCodes?.data?.length > 0) {
						setHasCostCodes(true);
					}
				};

				const fetchLogo = async () => {
					const logo = await Communication.getImageList();
					if (logo?.data?.length > 0) {
						setHasLogo(true);
					}
				};

				const fetchUsers = async () => {
					const users = await Communication.getUsers();
					if (users.data?.Users?.length > 0) {
						setHasUsers(true);
					}
				};

				// Execute all fetch operations in parallel
				const fetchPromises = [fetchContacts(), fetchCostCodes()];

				if (isBusiness) {
					fetchPromises.push(fetchLogo(), fetchUsers());
				}

				await Promise.all(fetchPromises);
			} catch (error) {
				console.error('Failed to fetch task completion status:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [isBusiness, customerId]);

	// Precompute Refund Assist capability once (used in filter below)
	type CapabilityArg = Parameters<typeof Session.hasCapability>[0];
	const hasRefundAssist = Session.hasCapability(
		Capability.RefundAssist as unknown as CapabilityArg
	);
	const isRefundAssistEnabled = Session.hasCapability(
		Capability.RefundAssistEnabled as unknown as CapabilityArg
	);

	// Filter out dismissed tasks and tasks that should be hidden based on plan ID
	const tasks = allTasks.filter(task => {
		// Filter out dismissed tasks
		if (dismissedState[task.id] === 1) {
			return false;
		}

		if (task.id === TASK_IDS.BUILD_ADDRESS_BOOK && hasContacts) {
			return false;
		}

		if (task.id === TASK_IDS.ADD_COST_CODES && hasCostCodes) {
			return false;
		}

		if (task.id === TASK_IDS.ADD_LOGO && hasLogo) {
			return false;
		}

		if (task.id === TASK_IDS.ADD_USERS && hasUsers) {
			return false;
		}

		// Filter out 'Add Logo' and 'Add Users' for business users for core plans
		if (task.id === TASK_IDS.ADD_LOGO || task.id === TASK_IDS.ADD_USERS) {
			if (!shouldShowLogoAndUsersTasks) return false;
		}

		// Refund Assist: show only when user has capability and has not enabled it yet
		if (task.id === TASK_IDS.REFUND_ASSIST) {
			return hasRefundAssist && !isRefundAssistEnabled;
		}

		// Review Subscription Plans: show only for new-lineup trial accounts
		if (task.id === TASK_IDS.REVIEW_SUBSCRIPTION_PLAN) {
			return (
				BillingPlans.isNewLineupPlan() && BillingPlans.isNewLineUpTrialPlan()
			);
		}

		return true;
	});

	const tasksToDisplay = 4;
	const hasMoreTasks = tasks.length > tasksToDisplay;
	const displayedTasks = isExpanded ? tasks : tasks.slice(0, tasksToDisplay);

	const handleTaskButtonClick = (taskId: TaskId, dataqa: string): void => {
		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'button',
				field_name_dataqa: dataqa,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});
		switch (taskId) {
			case TASK_IDS.STOCK_UP_ESSENTIALS:
				Navigation.navigateTo('supplies', { navType: 'newTab' });
				break;
			case TASK_IDS.BUILD_ADDRESS_BOOK:
				Navigation.navigateTo('contacts');
				break;
			case TASK_IDS.MOBILE_APP_DOWNLOAD:
				if (showModal) {
					showModal({
						component: MobileAppDownloadModal,
						data: {},
					});
				}
				break;
			case TASK_IDS.ADD_LOGO:
				Navigation.navigateTo('mail', { queryString: 'settings=labelImage' });
				break;
			case TASK_IDS.ADD_COST_CODES:
				Navigation.navigateTo('costcodes');
				break;
			case TASK_IDS.ADD_USERS:
				Navigation.navigateTo('users');
				break;
			case TASK_IDS.REFUND_ASSIST:
				Util.openHelpCenter('27608643172635');
				break;
			case TASK_IDS.REVIEW_SUBSCRIPTION_PLAN:
				Navigation.navigateTo('storeChangePlan');
				break;
		}
	};

	const handleTaskClose = (taskId: TaskId, dataqa: string): void => {
		// Clear any pending timeout for this task when manually closing
		if (timeoutRefs.current[taskId]) {
			clearTimeout(timeoutRefs.current[taskId]);
			delete timeoutRefs.current[taskId];
		}

		// Set task as dismissed (1) in the state object
		const newDismissedState: DismissedTasksState = {
			...dismissedState,
			[taskId]: 1,
		};
		setDismissedState(newDismissedState);

		// Save to user preferences
		saveDismissedTasksState(newDismissedState);

		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'button',
				field_name_dataqa: dataqa,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});
	};

	const handleTaskCheck = (taskId: TaskId, checked: boolean): void => {
		// Clear any existing timeout for this task
		if (timeoutRefs.current[taskId]) {
			clearTimeout(timeoutRefs.current[taskId]);
			delete timeoutRefs.current[taskId];
		}

		if (checked) {
			// Wait 3 seconds before dismissing the task
			timeoutRefs.current[taskId] = setTimeout(() => {
				handleTaskClose(taskId, `todo-task-item-checkbox-${taskId}`);
				delete timeoutRefs.current[taskId];
			}, 3000);
		}
	};

	const toggleExpanded = (): void => {
		setIsExpanded(prev => !prev);
	};

	if (isLoading) {
		return (
			<div className="todo-tasks-container loading">
				<Spinner msg="Loading tasks..." mask={false} />
			</div>
		);
	}

	return (
		<div className="todo-tasks-container">
			<div className="todo-tasks">
				{tasks.length === 0 ? (
					<div className="todo-tasks-empty-state">
						<div className="todo-tasks-empty-icon">
							<div className="todo-tasks-empty-icon-image" />
						</div>
						<Typography.H3>You're all caught up</Typography.H3>
						<Typography.P className="todo-tasks-empty-text">
							We'll still surface any important tasks here when needed.
						</Typography.P>
					</div>
				) : (
					<div className="todo-tasks-list">
						{displayedTasks.map(task => (
							<TodoTaskItem
								key={task.id}
								header={task.header}
								text={task.text}
								buttonText={task.buttonText}
								onButtonClick={() =>
									handleTaskButtonClick(task.id, `todo-task-item-${task.id}`)
								}
								onClose={() =>
									handleTaskClose(task.id, `todo-task-item-close-${task.id}`)
								}
								onCheck={checked => handleTaskCheck(task.id, checked)}
								isCompleted={task.isCompleted}
								dataqa={`todo-task-item-${task.id}`}
							/>
						))}
					</div>
				)}
			</div>

			{hasMoreTasks && (
				<div className="todo-tasks-footer">
					<WrappedButton
						label={isExpanded ? 'Fewer Tasks' : 'More Tasks'}
						buttonType={WrappedButtonType.TertiaryGhost}
						dataqa="todo-tasks-toggle"
						onClick={toggleExpanded}
						className={`todo-tasks-toggle ${isExpanded ? 'expanded' : ''}`}
					/>
				</div>
			)}
		</div>
	);
};

export default TodoTasks;
